/**
 * Spricht gerade schon genau diese Antwort? (02.10.2026, Ticket 2919)
 *
 * ## Der Anlass
 *
 * Journal 11:48 (Konto 1, PCM an): Die Sprechtasten-Antwort (271 Zeichen) spricht ihren ersten Satz per PCM, 2,5 s
 * später beginnt eine zweite Schlange (`wiedergabe(stream)`, ebenfalls 271 Zeichen) mit demselben Text von vorn:
 * `[tts-gap] via=abbruch:neuer-strom`, danach `Absage cache` für beide Sätze (die erste Schlange hatte sie schon
 * vertont) und derselbe erste Satz noch einmal — diesmal als Datei. Nach `[sprech-d] weicht der Sprechtaste`
 * suchte das Journal vergeblich: ein Strom OHNE Knopf weicht der laufenden Sprechtasten-Antwort
 * (lib/sprechtasten-vorrang.ts) und hätte diese Zeile geschrieben. Also kam der zweite Strom MIT Knopf — ein Tipp
 * auf den Vorlesen-Knopf der Blase. Der steht während einer Sprechtasten-Antwort auf „idle“ (nur der eigene Strom
 * setzt seinen Zustand), ein Tipp bedeutete „von vorn lesen“ und räumte die laufende Schlange ab („Doppel-Audio beim
 * Play-Klick“, stopCurrentTts). Mit PCM ist der erste Satz nach 1 s zu hören — und damit fällt das Doppel auf.
 *
 * ## Was hier steht
 *
 * Die Frage „liest die laufende Schlange genau diesen Text?“ an EINER Stelle. Der Knopf-Handler (app.ts) nimmt einen
 * Tipp dann als Pause/Weiter der laufenden Antwort, statt sie abzuräumen und noch einmal zu beginnen.
 */

/** Zwei Texte als gleich vergleichen: Leerraum zählt nicht. */
function norm(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

interface SprechSchlangeLesbar {
  isAudioActive(): boolean;
  sprichtText(): string;
}

interface SprechSchlangeSteuerbar extends SprechSchlangeLesbar {
  isPaused(): boolean;
  pause(): void;
  resume(): void;
}

/** Spricht (oder wartet) `schlange` gerade genau `sprechText`? */
export function wirdGeradeGesprochen(schlange: SprechSchlangeLesbar | null, sprechText: string): boolean {
  if (!schlange?.isAudioActive()) return false;
  const gesuchte = norm(sprechText);
  if (!gesuchte) return false;
  return norm(schlange.sprichtText()) === gesuchte;
}

/**
 * Ein Tipp auf den Vorlesen-Knopf einer Blase, deren Antwort GERADE gesprochen wird: Pause oder Weiter der
 * laufenden Schlange — kein Neubeginn. `true` heißt: erledigt, der Aufrufer liest nicht noch einmal vor.
 * `pille` setzt die Sprech-Pille („playing“/„paused“), `melde` schreibt eine Zeile ins Journal.
 */
export function tippAufLaufendeAntwort(
  schlange: SprechSchlangeSteuerbar | null,
  sprechText: string,
  pille: (zustand: "playing" | "paused") => void,
  melde: (zeile: string) => void,
): boolean {
  if (!schlange || !wirdGeradeGesprochen(schlange, sprechText)) return false;
  if (schlange.isPaused()) {
    schlange.resume();
    pille("playing");
    melde("[sprech-d] knopf-tipp auf die laufende Antwort: weiter");
  } else {
    schlange.pause();
    pille("paused");
    melde("[sprech-d] knopf-tipp auf die laufende Antwort: pausiert");
  }
  return true;
}
